// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierRecruits.jsx   ·   THE RECRUIT OUT FRONT OF EVERY BUILDING
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-09-24: "create a proper person infront of every building …
// Just have them stand in the front and you can interact with them to start
// their challenges etc … we're just putting placeholders nothing fancy."
//
// WHO stands where is a row in pierPlan.js (RECRUITS / RECRUIT_POS, surfaced as
// `recruit_<wing>` INTERACTABLES). THIS file only draws them.
//
// ⚑ THE LAW OF ONE BODY. The body is the kit's — IEBeing (ieBeingModels.jsx) or
// TerrorSpecies (ieTerrorSpecies.jsx), byte-identical to Annihilation's and the
// gamemaster master. Not one line of either kit is touched here. THEY OWN THE BODY,
// WE OWN THE POSE: this adapter only sizes it to a person, turns it to face the
// walk, and stands it on a faint ring of its wing's colour.
//
// ⚑ COST: 23 bodies. Each is a handful of primitives and cached canvases; a recruit
// more than FAR tiles from the walker is hidden (one useFrame for all of them).
// ════════════════════════════════════════════════════════════════════
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { IEBeing } from "./ieBeingModels";
import { TerrorSpecies } from "./ieTerrorSpecies";
import { poolTex } from "./pierPaint";

const HEIGHT = { beb: 1.35, champion: 1.7, lord: 2.05 };   // a person's scale on the pier (a walker is ~1.5)
const FAR = 34;                                             // beyond this, a recruit is not drawn

function Body({ r }) {
  return r.kit === "species"
    ? <TerrorSpecies being={r.being} tier={r.tier} />
    : <IEBeing being={r.being} tier={r.tier} />;
}

// one recruit: measured once and scaled to its tier's height, feet on the planks
function Recruit({ n, color }) {
  const outer = useRef(), inner = useRef();
  const r = n.recruit;
  useLayoutEffect(() => {
    const g = inner.current;
    if (!g) return;
    g.scale.setScalar(1); g.position.set(0, 0, 0); g.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(g);
    if (box.isEmpty()) return;
    const h = box.max.y - box.min.y;
    const k = h > 0.05 ? HEIGHT[r.tier] / h : 1;
    g.scale.setScalar(k);
    g.position.y = -box.min.y * k;                           // feet on the deck, whatever the body's origin
  }, [r.kit, r.being, r.tier]);
  // a building facing S sends its recruit to face S (+z, the kit's own forward); N → turn round
  const rotY = n.face === "N" ? Math.PI : 0;
  const ring = useMemo(() => new THREE.MeshBasicMaterial({
    color, transparent: true, opacity: 0.28, depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending,
  }), [color]);
  // ⚠ A POOL OF ITS WING'S LIGHT, NOT A LAMP. The canon bodies are painted for
  // Annihilation's lit fields; on the pier at night a violet Lord read as a
  // silhouette (visual review, 2026-09-24). The light budget is three, forever
  // (Pier3D), so no pointLight: the planks under a recruit glow instead, the way
  // a doorway's spill does — and the body reads against it.
  const pool = useMemo(() => new THREE.MeshBasicMaterial({
    map: poolTex(), color, transparent: true, opacity: 0.55, depthWrite: false, toneMapped: false,
    blending: THREE.AdditiveBlending,
  }), [color]);
  useEffect(() => () => { ring.dispose(); pool.dispose(); }, [ring, pool]);
  return (
    <group ref={outer} position={[n.x, 0, n.y]} rotation={[0, rotY, 0]} userData={{ recruit: true }}>
      <group ref={inner}><Body r={r} /></group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]} material={pool}>
        <planeGeometry args={[3.2, 3.2]} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.012, 0]} material={ring}>
        <ringGeometry args={[0.46, 0.62, 28]} />
      </mesh>
    </group>
  );
}

// ⭐ Every recruit on the pier. `colorOf(wing)` gives the wing's portal colour.
export function PierRecruits({ interactables, colorOf, posRef }) {
  const list = useMemo(() => interactables.filter((n) => n.recruit), [interactables]);
  const root = useRef();
  useFrame(() => {
    const g = root.current, p = posRef && posRef.current;
    if (!g || !p) return;
    for (const c of g.children) {
      const d = Math.hypot(c.position.x - p.x, c.position.z - p.y);
      c.visible = d < FAR;
    }
  });
  return (
    <group ref={root}>
      {list.map((n) => <Recruit key={n.id} n={n} color={colorOf(n.recruit.wing)} />)}
    </group>
  );
}
export default PierRecruits;
